import type { accountingType } from '@/features/master-table/models';
import {
  type ReportOrganizingTableFormat3ProjectModel,
  format3ColumnType,
} from '@/features/mold/models/organizing-table';
import type { projectDetailUrlTypeEnum } from '@/features/mold/models/report';
import { getProjectDetailPageUrl } from '@/features/project/_utils/project';
import { ActionOfReflectionStatusLabelModifier } from '@/features/project/projects/_models/project';
import { t } from '@/i18n';
import {
  convertToNegative,
  createCsv,
  formatAmountReport,
  validateTotalWithElements,
} from '@/utils';
import type { z } from 'zod';

type ProjectData = z.infer<typeof ReportOrganizingTableFormat3ProjectModel>;

export const format3ValueConverter = {
  accountingTypeName: (
    accountingTypeType: z.infer<typeof accountingType> | null,
    accountingTypeName: string | null,
    accountTitleName?: string | null,
  ) => {
    if (!accountingTypeType || !accountingTypeName) {
      return '';
    }
    return accountingTypeType === 'general'
      ? accountingTypeName
      : `${accountingTypeName}${accountTitleName ? ` ${accountTitleName}` : ''}`;
  },
  previousFinalizedAmountTotal: ({
    data,
    type,
    isDebugMode,
  }: { data: ProjectData; type: 'csv' | 'pdf'; isDebugMode: boolean }) =>
    formatAmountReport(data.previousFinalizedAmountTotal, type, {
      negativeCount: data.negativePreviousFinalizedAmountTotalCount,
      isDebugMode,
      rawValue:
        type === 'pdf' ? data.rawPreviousFinalizedAmountTotal : undefined,
    }),
  previousExecutionAmountTotal: ({
    data,
    type,
    isDebugMode,
  }: { data: ProjectData; type: 'csv' | 'pdf'; isDebugMode: boolean }) =>
    formatAmountReport(data.previousExecutionAmountTotal, type, {
      negativeCount: data.negativePreviousExecutionAmountTotalCount,
      isDebugMode,
      rawValue:
        type === 'pdf' ? data.rawPreviousExecutionAmountTotal : undefined,
    }),
  reflectedAmount: ({
    data,
    type,
    isDebugMode,
  }: { data: ProjectData; type: 'csv' | 'pdf'; isDebugMode: boolean }) =>
    formatAmountReport(data.reflectedAmount, type, {
      negativeCount: data.negativeReflectedAmountCount,
      isDebugMode,
      rawValue: type === 'pdf' ? data.rawReflectedAmount : undefined,
    }),
  requestedAmount: ({
    data,
    type,
    isDebugMode,
  }: { data: ProjectData; type: 'csv' | 'pdf'; isDebugMode: boolean }) =>
    formatAmountReport(data.requestedAmount, type, {
      negativeCount: data.negativeRequestedAmountCount,
      isDebugMode,
      rawValue: type === 'pdf' ? data.rawRequestedAmount : undefined,
    }),
  finalizedAmount: ({
    data,
    type,
    isDebugMode,
  }: { data: ProjectData; type: 'csv' | 'pdf'; isDebugMode: boolean }) =>
    formatAmountReport(data.finalizedAmount, type, {
      negativeCount: data.negativeFinalizedAmountCount,
      isDebugMode,
      rawValue: type === 'pdf' ? data.rawFinalizedAmount : undefined,
    }),
  differenceBetweenRequestedAmountAndInitialBudget: ({
    data,
    type,
    isDebugMode,
  }: { data: ProjectData; type: 'csv' | 'pdf'; isDebugMode: boolean }) => {
    const { result } = validateTotalWithElements({
      totalValue: data.differenceBetweenRequestedAmountAndInitialBudget,
      elements: [
        {
          value: data.requestedAmount,
          rawValue: data.rawRequestedAmount,
          negativeCount: data.negativeRequestedAmountCount,
        },
        {
          value: convertToNegative(data.finalizedAmount),
          rawValue: convertToNegative(data.rawFinalizedAmount),
          negativeCount: data.negativeFinalizedAmountCount,
        },
      ],
    });

    return formatAmountReport(
      data.differenceBetweenRequestedAmountAndInitialBudget,
      type,
      {
        isDebugMode,
        negativeCount: result === 'isNull' ? 1 : 0,
      },
    );
  },
};

//////////////////////////////////////////////////////////////////////////////////////////////////////
// CSV
//////////////////////////////////////////////////////////////////////////////////////////////////////

type CsvHeaderKeyType = z.infer<typeof format3ColumnType>;

export const getformat3HeaderModifier = ({
  year,
  isPdf,
}: {
  year: number;
  isPdf?: boolean;
}) =>
  ({
    name: t('project-name'),
    projectNumber: t('project-number'),
    previousFinalizedAmountTotal: isPdf
      ? t('budgetary-sum')
      : t('budgetary-sum-csv', {
          year: year - 1,
        }),
    previousExecutionAmountTotal: isPdf
      ? t('execution-amount')
      : t('execution-amount-csv', {
          year: year - 1,
        }),
    resultOfPublishing: t('result-of-publishing'),
    finalizedAmount: t('report-organizing-table-header-finalized-amount', {
      year,
    }),
    requestedAmount: t('report-organizing-table-header-reflected-amount', {
      year: year + 1,
    }),
    differenceBetweenRequestedAmountAndInitialBudget: t(
      'difference-between-requested-amount-and-initial-budget',
    ),
    accountingTypeName: t('accounting-type'),
    reflectedAmount: t('reflected-amount'),
    reflectionStatusAction: t('improvement-and-reflection-status'),
    reflectionStatusContext: t('details'),
    projectDetailPageUrl: t('project-detail-page-url'),
  }) satisfies Record<CsvHeaderKeyType, string>;

export const getFormat3CsvData = (args: {
  year: number;
  data: Array<z.infer<typeof ReportOrganizingTableFormat3ProjectModel>>;
  isDebugMode: boolean;
  projectDetailUrlType?: z.infer<typeof projectDetailUrlTypeEnum> | null;
}) => {
  const headerModifier = getformat3HeaderModifier({ year: args.year });

  const csvBody = args.data.flatMap<Record<CsvHeaderKeyType, string | number>>(
    (data) => ({
      name: data.name,
      projectNumber: data.projectNumber,
      previousFinalizedAmountTotal:
        format3ValueConverter.previousFinalizedAmountTotal({
          data,
          type: 'csv',
          isDebugMode: args.isDebugMode,
        }),
      previousExecutionAmountTotal:
        format3ValueConverter.previousExecutionAmountTotal({
          data,
          type: 'csv',
          isDebugMode: args.isDebugMode,
        }),
      resultOfPublishing: data.resultOfPublishing ?? '',
      finalizedAmount: format3ValueConverter.finalizedAmount({
        data,
        type: 'csv',
        isDebugMode: args.isDebugMode,
      }),
      requestedAmount: format3ValueConverter.requestedAmount({
        data,
        type: 'csv',
        isDebugMode: args.isDebugMode,
      }),
      differenceBetweenRequestedAmountAndInitialBudget:
        format3ValueConverter.differenceBetweenRequestedAmountAndInitialBudget({
          data,
          type: 'csv',
          isDebugMode: args.isDebugMode,
        }) ?? '',
      accountingTypeName: format3ValueConverter.accountingTypeName(
        data.accountingTypeType,
        data.accountingTypeName,
        data.accountTitleName,
      ),
      reflectedAmount: format3ValueConverter.reflectedAmount({
        data,
        type: 'csv',
        isDebugMode: args.isDebugMode,
      }),
      reflectionStatusAction: data.reflectionStatusAction
        ? ActionOfReflectionStatusLabelModifier[data.reflectionStatusAction]
        : '',
      reflectionStatusContext: data.reflectionStatusContext ?? '',
      projectDetailPageUrl: getProjectDetailPageUrl({
        isPS: args.projectDetailUrlType === 'public',
        projectId: data.id,
      }),
    }),
  );

  return createCsv({
    headerKeys: format3ColumnType.options,
    headerConverter: headerModifier,
    body: csvBody,
  });
};
